import { relations, sql } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { numericAsNumber } from '../../custom-type'
import { organization, user } from '../auth'
import { Invoice } from '../membership/invoice'
import { Premium } from '../membership/premium'
import { SeatEntitlementStatusEnum, SeatSourceTypeEnum } from './enums'
import { Funder } from './funder'
import { Program } from './program'

/**
 * A first-class bundle of Premium seats provisioned to ONE school for a fixed period.
 * The fulfillment / access layer (separate from the money layer). Its `id` is the reporting
 * scope key stamped onto every seat activated under it (`premium.entitlementId`).
 *
 * - Grant family (`funderId` may be set, `invoiceId` null): impact/sponsor reporting.
 * - `direct_paid` (`invoiceId` set to the internal invoice, `funderId` null): revenue; excluded
 *   from impact reporting.
 *
 * Terms (seats/dates) are fixed once active — to give more, create a follow-on entitlement.
 * `endDate` is derived server-side from `startDate` + `durationMonths` and never edited directly.
 */
export const SeatEntitlement = pgTable(
  'seat_entitlement',
  {
    id: uuid().primaryKey().defaultRandom(),
    sourceType: SeatSourceTypeEnum().notNull(),
    name: text().notNull(),
    funderId: uuid().references(() => Funder.id, { onDelete: 'restrict' }),
    invoiceId: uuid().references(() => Invoice.id, { onDelete: 'restrict' }),
    programId: uuid().references(() => Program.id, { onDelete: 'set null' }),
    assignedSchoolId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'restrict' }),
    /**
     * Legacy per-seat value. Superseded by `totalValue` — kept only so rows written before the
     * switch still satisfy the NOT NULL constraint. New writes fill it with the derived per-seat
     * figure; nothing should read it except `entitlementTotalValue`'s fallback.
     *
     * @deprecated Drop once `total_value` is backfilled and made NOT NULL.
     */
    seatValue: numericAsNumber().notNull(),
    /**
     * The agreed value of the whole bundle, in whole rupiah. The total is what an admin actually
     * types and what a sponsor agrees to, so it — not a per-seat quotient — is what gets stored.
     * Per-seat is derived on read (`perSeatValue`), free to round without corrupting the total.
     *
     * Nullable during the transition: rows created before this column existed have no total, and
     * `entitlementTotalValue` reconstructs one for them.
     */
    totalValue: numericAsNumber(),
    totalSeats: integer().notNull(),
    durationMonths: integer().notNull(),
    startDate: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    endDate: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    status: SeatEntitlementStatusEnum().notNull().default('draft'),
    externalReference: text(),
    expiryNoticeSent: boolean().notNull().default(false),
    notes: text(),
    createdBy: text().references(() => user.id, { onDelete: 'set null' }),
    activatedAt: timestamp({ mode: 'date', withTimezone: true }),
    revokedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_seat_entitlement_school').on(t.assignedSchoolId),
    index('idx_seat_entitlement_program').on(t.programId),
    index('idx_seat_entitlement_funder').on(t.funderId),
    index('idx_seat_entitlement_status').on(t.status),
    // Composite for the expiry sweep (status = 'active' AND end_date within N days)
    index('idx_seat_entitlement_status_end_date').on(t.status, t.endDate),
    index('idx_seat_entitlement_source_type').on(t.sourceType),
  ],
)

export const seatEntitlementRelations = relations(SeatEntitlement, ({ one, many }) => ({
  funder: one(Funder, {
    fields: [SeatEntitlement.funderId],
    references: [Funder.id],
  }),

  invoice: one(Invoice, {
    fields: [SeatEntitlement.invoiceId],
    references: [Invoice.id],
  }),

  program: one(Program, {
    fields: [SeatEntitlement.programId],
    references: [Program.id],
  }),

  assignedSchool: one(organization, {
    fields: [SeatEntitlement.assignedSchoolId],
    references: [organization.id],
  }),

  createdByUser: one(user, {
    fields: [SeatEntitlement.createdBy],
    references: [user.id],
  }),

  premiums: many(Premium),
}))

export const InsertSeatEntitlementSchema = createInsertSchema(SeatEntitlement, {
  sourceType: z.enum(SeatSourceTypeEnum.enumValues),
  name: z.string().min(1),
  funderId: z.uuid().optional(),
  invoiceId: z.uuid().optional(),
  programId: z.uuid().optional(),
  assignedSchoolId: z.string().min(1),
  seatValue: z.number().nonnegative(),
  totalValue: z.number().int().nonnegative(),
  totalSeats: z.number().int().positive(),
  durationMonths: z.number().int().positive(),
  startDate: z.date(),
  endDate: z.date(),
  externalReference: z.string().optional(),
  expiryNoticeSent: z.boolean().default(false),
  notes: z.string().optional(),
  createdBy: z.string().optional(),
})
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    if (data.endDate <= data.startDate) {
      ctx.addIssue({ code: 'custom', message: 'End date must be after start date', path: ['endDate'] })
    }

    // Revenue family (direct_paid) links a transaction, never a funder.
    if (data.sourceType === 'direct_paid') {
      if (data.funderId) {
        ctx.addIssue({ code: 'custom', message: 'direct_paid entitlements cannot have a funder', path: ['funderId'] })
      }
    } else {
      // Grant family never links a transaction.
      if (data.invoiceId) {
        ctx.addIssue({
          code: 'custom',
          message: 'Grant-family entitlements cannot link an invoice',
          path: ['invoiceId'],
        })
      }
    }
  })

export const UpdateSeatEntitlementSchema = createUpdateSchema(SeatEntitlement, {
  name: z.string().min(1),
  status: z.enum(SeatEntitlementStatusEnum.enumValues),
  notes: z.string().nullable(),
  expiryNoticeSent: z.boolean(),
  activatedAt: z.date().nullable(),
  revokedAt: z.date().nullable(),
})
  .omit({
    id: true,
    sourceType: true,
    funderId: true,
    invoiceId: true,
    programId: true,
    assignedSchoolId: true,
    seatValue: true,
    totalValue: true,
    totalSeats: true,
    durationMonths: true,
    startDate: true,
    endDate: true,
    externalReference: true,
    createdBy: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectSeatEntitlementSchema = createSelectSchema(SeatEntitlement, {
  id: z.uuid(),
  sourceType: z.enum(SeatSourceTypeEnum.enumValues),
  name: z.string(),
  funderId: z.uuid().nullable(),
  invoiceId: z.uuid().nullable(),
  programId: z.uuid().nullable(),
  assignedSchoolId: z.string(),
  seatValue: z.number(),
  totalValue: z.number().nullable(),
  totalSeats: z.number().int(),
  durationMonths: z.number().int(),
  startDate: z.date(),
  endDate: z.date(),
  status: z.enum(SeatEntitlementStatusEnum.enumValues),
  externalReference: z.string().nullable(),
  expiryNoticeSent: z.boolean(),
  notes: z.string().nullable(),
  createdBy: z.string().nullable(),
  activatedAt: z.date().nullable(),
  revokedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertSeatEntitlement = typeof SeatEntitlement.$inferInsert
export type UpdateSeatEntitlement = z.infer<typeof UpdateSeatEntitlementSchema>
export type SelectSeatEntitlement = typeof SeatEntitlement.$inferSelect

/**
 * The agreed total in whole rupiah.
 *
 * Rows written before `total_value` existed only carry the rounded per-seat quotient, so their
 * total has to be reconstructed — rounded, because that stray fraction is precisely the bug
 * (2.650.000 / 45 -> 58888.89 -> 2.650.000,05). Same formula the eventual backfill will use, so
 * legacy and new rows read identically before and after it runs.
 */
export function entitlementTotalValue(
  entitlement: Pick<SelectSeatEntitlement, 'totalValue' | 'seatValue' | 'totalSeats'>,
): number {
  return entitlement.totalValue ?? Math.round(entitlement.seatValue * entitlement.totalSeats)
}

/**
 * Value of a single seat, derived from the total. Display-only: never persist this as the source
 * of truth or multiply it back out — that round-trip is what leaked cents in the first place.
 */
export function perSeatValue(entitlement: { totalValue: number; totalSeats: number }): number {
  return entitlement.totalSeats > 0 ? entitlement.totalValue / entitlement.totalSeats : 0
}
